import React, { createContext, useContext, useEffect, useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { Pin, Plus } from 'lucide-react';
import apiClient from '../../utils/apiClient';
import './PlanningNotices.css';

export interface PlanningNotice {
  id: number; parish_id: number; date: string; service_id: number | null;
  title: string; details: string; revision: number;
}
interface Parish { id: number; name: string; church_ids: number[] }
interface NoticeService { service_id: number; parish_id: number; date: string; time: string; title: string; church_name: string }
interface NoticeData { notices: PlanningNotice[]; parishes: Parish[]; services: NoticeService[] }
interface Draft {
  id?: number; revision?: number; parish_id: number; date: string;
  service_id: number | null; title: string; details: string;
}
interface NoticeContext extends NoticeData {
  loading: boolean; error: string; message: string; reload: () => Promise<void>;
  create: (serviceId?: number) => void; edit: (notice: PlanningNotice) => void;
}
const Context = createContext<NoticeContext | null>(null);
const emptyData: NoticeData = { notices: [], parishes: [], services: [] };
const localToday = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};
const dateLabel = (date: string) => new Date(date+'T12:00:00').toLocaleDateString('en-US', { month:'short',day:'numeric',year:'numeric' });
const errorMessage = (error: any) => {
  const message = error?.response?.data?.error;
  return typeof message === 'string' ? message : message?.message || 'Could not load or save notices. Please try again.';
};

export function PlanningNoticesProvider({ children }: { children: React.ReactNode }) {
  const [data,setData] = useState<NoticeData>(emptyData);
  const [loading,setLoading] = useState(true);
  const [error,setError] = useState('');
  const [message,setMessage] = useState('');
  const [draft,setDraft] = useState<Draft | null>(null);
  const [saving,setSaving] = useState(false);
  const [formError,setFormError] = useState('');
  const [confirmDelete,setConfirmDelete] = useState(false);
  const reload = async () => {
    setError('');
    try { setData((await apiClient.get<NoticeData>('/planning/notices')).data); }
    catch (e) { setError(errorMessage(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { void reload(); }, []);
  const open = (value: Draft) => {
    setDraft(value); setFormError(''); setConfirmDelete(false); setMessage('');
  };
  const create = (serviceId?: number) => {
    const service = data.services.find(s => s.service_id === serviceId);
    const parish = service?.parish_id || data.parishes[0]?.id;
    if (parish) open({ parish_id:parish, date:service?.date || localToday(), service_id:null, title:'',details:'' });
  };
  const edit = (notice: PlanningNotice) => open({
    id:notice.id, revision:notice.revision, parish_id:notice.parish_id, date:notice.date,
    service_id:notice.service_id, title:notice.title, details:notice.details,
  });
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!draft || saving) return;
    setSaving(true); setFormError('');
    try {
      const { id,revision,...fields } = draft;
      fields.title = fields.title.trim();
      const saved = id
        ? (await apiClient.put<PlanningNotice>(`/planning/notices/${id}`,{...fields,revision})).data
        : (await apiClient.post<PlanningNotice>('/planning/notices',fields)).data;
      setData(prev => ({...prev,notices:[...prev.notices.filter(n => n.id !== saved.id),saved]
        .sort((a,b) => a.date.localeCompare(b.date) || a.id-b.id)}));
      setMessage(`“${saved.title}” saved for ${dateLabel(saved.date)}.`);
      setDraft(null);
    } catch (e) { setFormError(errorMessage(e)); }
    finally { setSaving(false); }
  };
  const remove = async () => {
    if (!draft?.id || saving) return;
    setSaving(true); setFormError('');
    try {
      await apiClient.delete(`/planning/notices/${draft.id}`,{ data:{parish_id:draft.parish_id,revision:draft.revision} });
      setData(prev => ({...prev,notices:prev.notices.filter(n => n.id !== draft.id)}));
      setMessage('Notice deleted.'); setDraft(null);
    } catch (e) { setFormError(errorMessage(e)); }
    finally { setSaving(false); }
  };
  const close = () => { if (!saving) setDraft(null); };
  const choices = draft ? data.services.filter(s => s.parish_id === draft.parish_id && s.date === draft.date) : [];
  return <Context.Provider value={{...data,loading,error,message,reload,create,edit}}>
    {children}
    <Modal show={!!draft} onHide={close} centered backdrop={saving ? 'static' : true} keyboard={!saving}>
      <Modal.Header closeButton={!saving}><Modal.Title>{draft?.id ? 'Edit planning notice' : 'Add planning notice'}</Modal.Title></Modal.Header>
      {draft && <Form onSubmit={save}>
        <Modal.Body>
          {formError && <Alert variant="danger" role="alert">{formError}
            <div><Button variant="link" size="sm" onClick={() => void reload()}>Reload saved notices</Button>
              <span className="small">Your draft will stay open. Close and reopen it to use a newer saved version.</span></div>
          </Alert>}
          <fieldset disabled={saving}>
            {data.parishes.length > 1 && <Form.Group className="mb-3" controlId="notice-parish">
              <Form.Label>Parish</Form.Label><Form.Select value={draft.parish_id} disabled={!!draft.id}
                onChange={e => setDraft({...draft,parish_id:Number(e.target.value),service_id:null})}>
                {data.parishes.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
              </Form.Select>
            </Form.Group>}
            <Form.Group className="mb-3" controlId="notice-date">
              <Form.Label>Date</Form.Label><Form.Control type="date" required value={draft.date}
                onChange={e => setDraft({...draft,date:e.target.value,service_id:null})} />
            </Form.Group>
            <Form.Group className="mb-3" controlId="notice-scope">
              <Form.Label>Applies to</Form.Label><Form.Select value={draft.service_id ?? ''}
                onChange={e => setDraft({...draft,service_id:e.target.value ? Number(e.target.value) : null})}>
                <option value="">All parish services on this date</option>
                {choices.map(s => <option key={s.service_id} value={s.service_id}>{s.church_name} · {s.time} · {s.title}</option>)}
              </Form.Select>
              {!choices.length && <Form.Text>You can save this now. It will appear when services are planned for this date.</Form.Text>}
            </Form.Group>
            <Form.Group className="mb-3" controlId="notice-title">
              <Form.Label>Title</Form.Label><Form.Control required maxLength={160} autoFocus
                placeholder="e.g. Bishop’s visit" value={draft.title}
                onChange={e => setDraft({...draft,title:e.target.value})} />
            </Form.Group>
            <Form.Group controlId="notice-details">
              <Form.Label>Details <span className="text-muted">(optional)</span></Form.Label>
              <Form.Control as="textarea" rows={4} maxLength={5000} value={draft.details}
                onChange={e => setDraft({...draft,details:e.target.value})} />
            </Form.Group>
          </fieldset>
          {confirmDelete && <Alert variant="warning" className="mt-3 mb-0">
            Delete “{draft.title}” from the planning page?
            <div className="mt-2"><Button variant="danger" size="sm" disabled={saving} onClick={() => void remove()}>Delete notice</Button>
              <Button variant="link" size="sm" disabled={saving} onClick={() => setConfirmDelete(false)}>Keep notice</Button></div>
          </Alert>}
        </Modal.Body>
        <Modal.Footer>
          {draft.id && !confirmDelete && <Button variant="outline-danger" className="me-auto" disabled={saving}
            onClick={() => setConfirmDelete(true)}>Delete</Button>}
          <Button variant="secondary" onClick={close} disabled={saving}>Cancel</Button>
          <Button type="submit" disabled={saving || !draft.title.trim() || !draft.date}>{saving ? 'Saving…' : 'Save notice'}</Button>
        </Modal.Footer>
      </Form>}
    </Modal>
  </Context.Provider>;
}

export function PlanningNoticeActions() {
  const ctx = useContext(Context);
  const [showSaved,setShowSaved] = useState(false);
  if (!ctx) return null;
  if (ctx.loading) return <span className="planning-notice-status">Loading notices…</span>;
  if (ctx.error) return <div className="planning-notice-error" role="alert">{ctx.error}
    <button type="button" onClick={() => void ctx.reload()}>Retry</button></div>;
  if (!ctx.parishes.length) return null;
  return <div className="planning-notice-actions">
    <button type="button" className="action-button" onClick={() => ctx.create()}><Plus size={16} /> Add notice</button>
    <button type="button" className="action-button" onClick={() => setShowSaved(!showSaved)} aria-expanded={showSaved}>
      <Pin size={16} /> Saved notices ({ctx.notices.length})
    </button>
    {ctx.message && <span className="planning-notice-status" role="status">{ctx.message}</span>}
    <Modal show={showSaved} onHide={() => setShowSaved(false)} centered>
      <Modal.Header closeButton><Modal.Title>Saved planning notices</Modal.Title></Modal.Header>
      <Modal.Body>
        {ctx.notices.length ? <ul className="planning-notice-list">
          {ctx.notices.map(n => <li key={n.id}><button type="button" onClick={() => {setShowSaved(false);ctx.edit(n);}}>
            <strong>{n.title}</strong><span>{dateLabel(n.date)} · {n.service_id ? 'One service' : 'All parish services'}</span>
          </button></li>)}
        </ul> : <p>No notices yet. Add a date and title to keep something visible while planning.</p>}
      </Modal.Body>
      <Modal.Footer><Button onClick={() => {setShowSaved(false);ctx.create();}}>Add notice</Button></Modal.Footer>
    </Modal>
  </div>;
}

export function PlanningNoticeStrip({ serviceId, compact=false }: { serviceId:number; compact?:boolean }) {
  const ctx = useContext(Context);
  if (!ctx || ctx.loading || ctx.error) return null;
  const service = ctx.services.find(s => s.service_id === serviceId);
  if (!service) return null;
  const notices = ctx.notices.filter(n => n.parish_id === service.parish_id && n.date === service.date &&
    (n.service_id === null || n.service_id === serviceId));
  return <div className={`planning-notice-strip ${compact ? 'planning-notice-compact' : ''}`}>
    {notices.map(n => <button type="button" className="planning-notice" key={n.id} onClick={() => ctx.edit(n)}
      aria-label={`Edit notice: ${n.title}`} title={n.details || 'View or edit notice'}>
      <Pin size={15} aria-hidden="true" /><span><strong>{n.title}</strong>
        {!compact && n.details && <span className="planning-notice-preview">{n.details}</span>}</span>
    </button>)}
    <button type="button" className="planning-notice-add" onClick={() => ctx.create(serviceId)}
      aria-label={`Add notice for ${dateLabel(service.date)}`}><Plus size={13} aria-hidden="true" /> Add notice</button>
  </div>;
}
